import React, { useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import Utils from '../utils/utils';
import { RATIOS, BUCKET } from '../utils/config';

const SRGLetterboxPlayer = ({
  URN,
  env,
  lang,
  branch,
  debug,
  continuousPlayback,
  recommendations,
  resizable,
  ratio,
  setLetterbox,
  isMobile,
  isPlayerBar,
  standalone,
  pendingSeek,
  castAppId,
  hardConfiguration,
  storage,
  subtitle,
  api,
}) => {
  const [version, setVersion] = useState('');
  const [letterboxPlayer, setLetterboxPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewApi, setIsNewApi] = useState(false);

  const playerBaseUrl = useCallback(() => {
    if (branch === 'local') {
      return 'http://localhost:1234';
    } else {
      return `https://${BUCKET}/${branch}`;
    }
  }, [branch]);

  const initializeError = useCallback(() => {
    alert(`An error occured while loading ${branch} branch.`);
    api.setDefaultBranch();
  }, [api, branch]);

  const createPlayer = useCallback((newApi, conf = null) => {
    const optionsParam = conf || {
      debug: debug,
      continuousPlayback: Utils.formatNewApiConfigParameter(newApi, storage, continuousPlayback),
      recommendations: Utils.formatNewApiConfigParameter(newApi, storage, recommendations),
      ilHost: env,
      playerFocus: true,
      language: lang,
      chromeCastReceiver: castAppId,
      container: '#srgletterbox-player',
      textTrackLanguage : Utils.formatNewApiConfigParameter(newApi, storage, subtitle)
    };

    const lbPlayer = new window.SRGLetterbox(optionsParam);
    if (newApi) {
      lbPlayer.loadUrn(URN, { pendingSeek, standalone });
    } else {
      lbPlayer.initializeOnElement('srgletterbox-player');
      lbPlayer.prepareToPlayURN(URN, pendingSeek, undefined, undefined, standalone);
    }
    window.letterbox = lbPlayer;
    setLetterboxPlayer(lbPlayer);
    setLetterbox(lbPlayer);
  }, [
    pendingSeek,
    standalone,
    URN,
    env,
    lang,
    debug,
    continuousPlayback,
    recommendations,
    setLetterbox,
    storage,
    subtitle,
    castAppId,
  ]);

  const initializePlayer = useCallback(() => {
    const [majorVersion] = window.SRGLetterbox.VERSION.split('.');
    const { storageNamespace } = window.SRGLetterbox.LETTERBOX_DEFAULT_OPTIONS;
    const version = window.SRGLetterbox.LONG_VERSION || '';
    const newApi = parseInt(majorVersion, 10) === 3 || storageNamespace !== undefined

    setVersion(version);
    setIsNewApi(newApi);

    createPlayer(newApi);

    setLoading(false);
  }, [setVersion, setIsNewApi, createPlayer, setLoading]);

  const mountPlayer = useCallback(() => {
    const script = document.createElement('script');
    const cacheBuster = Math.round(new Date().getTime() / 1000);

    script.async = true;
    script.src = `${playerBaseUrl()}/letterbox.js?c=${cacheBuster}`;
    script.type = 'text/javascript';

    script.onload = initializePlayer;
    script.onerror = initializeError;

    document.body.appendChild(script);

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = `${playerBaseUrl()}/letterbox.css?c=${cacheBuster}`;
    link.media = 'all';

    document.head.appendChild(link);
  }, [playerBaseUrl, initializePlayer, initializeError]);

  const unmountPlayer = useCallback(() => {
    if (!loading) {
      setLoading(true);
      letterboxPlayer.player.dispose();
      Utils.removeFile('letterbox.js', 'js');
      Utils.removeFile('letterbox.css', 'css');
      setLetterboxPlayer(null);
      setLetterbox(null);
    }
  }, [letterboxPlayer, setLetterbox, loading, setLoading]);

  useEffect(() => {
    if (letterboxPlayer) {
      unmountPlayer();
    }
    mountPlayer();
    //eslint-disable-next-line
  }, [lang, branch, env, debug, continuousPlayback, recommendations, storage, castAppId, subtitle]);

  useEffect(() => {
    if (letterboxPlayer) {
      console.log(letterboxPlayer);
      if (isNewApi) {
        letterboxPlayer.loadUrn(URN, { pendingSeek, standalone });
      } else {
        letterboxPlayer.prepareToPlayURN(URN, pendingSeek, undefined, undefined, standalone);
      }
    }
    //eslint-disable-next-line
  }, [URN, pendingSeek, standalone, isNewApi]);

  useEffect(() => {
    if (!letterboxPlayer) {
      return;
    }
    if (isMobile) {
      letterboxPlayer.player.el().classList.add('srgssr-touch-enabled');
    } else {
      letterboxPlayer.player.el().classList.remove('srgssr-touch-enabled');
    }
  }, [isMobile, letterboxPlayer]);

  useEffect(() => {
    const { format } = RATIOS[ratio];
    if (letterboxPlayer) {
      letterboxPlayer.aspectRatio(format);
    }
  }, [ratio, letterboxPlayer]);

  useEffect(() => {
    if (letterboxPlayer) {
      letterboxPlayer.isNewApi = isNewApi;
    }
  }, [isNewApi, letterboxPlayer]);

  useEffect(() => {
    if (letterboxPlayer) {
      letterboxPlayer.player.dispose();
      createPlayer(true, hardConfiguration);
    }
    //eslint-disable-next-line
  }, [hardConfiguration]);

  return (
    <>
      <div
        id="srgletterbox-player"
        className={classNames({ player_bar: isPlayerBar, resizable: resizable })}
      ></div>
      <p className="player__version">
        {loading && <span>Loading...</span>}
        {!loading && (
          <>
            Version : <span>{version}</span>
          </>
        )}
      </p>
    </>
  );
};

export default SRGLetterboxPlayer;
